"use client"

import * as React from "react"

/**
 * The engine's own chrome strings, supplied by the host app.
 *
 * The engine used to call `useTranslations("dashboard.engine")` directly, which bound it to one app's i18n
 * entrypoint (`@/i18n` in tenant, `@/i18n/client` in admin) and made it unusable from anywhere else. It is
 * a layout engine; it has no business knowing how a host resolves a locale.
 *
 * Passed through context rather than props because `WidgetContainer` sits several levels down the drag-and-
 * drop tree (`DashboardLayoutView` → `SortableWidget` → `WidgetContainer`), and threading twenty strings
 * through that would put i18n plumbing in the signature of every layout component.
 *
 * `addNamed` is a function because it interpolates the widget's title; the rest are plain strings, matching
 * the convention in `@ibada/overview-widgets` where every widget takes text already formatted by the caller.
 */
export interface DashboardEngineLabels {
  configure: string;
  editing: string;
  addWidgets: string;
  done: string;
  addDescription: string;
  allVisible: string;
  allVisibleHint: string;
  addShort: string;
  addNamed: (name: string) => string;
  hide: string;
  resize: string;
  dragToMove: string;
  refreshing: string;
  actions: string;
  refresh: string;
  viewDetails: string;
  expand: string;
  contentError: string;
  contentEmpty: string;
  spans: { small: string; medium: string; large: string };
  /** Optional so hosts that offer no reset need not supply it. */
  reset?: string;
}

/**
 * English defaults. Present so the engine renders standalone — in a package test, a Storybook story, or an
 * app that has not wired its provider yet — instead of crashing or showing raw keys. A host that cares about
 * locale supplies its own; one that forgets gets readable English rather than a broken screen.
 */
export const DEFAULT_DASHBOARD_ENGINE_LABELS: DashboardEngineLabels = {
  configure: "Configure",
  editing: "Editing dashboard",
  addWidgets: "Add widgets",
  done: "Done",
  addDescription: "Bring hidden widgets back to your dashboard.",
  allVisible: "All widgets are visible",
  allVisibleHint: "Hide a widget to see it listed here.",
  addShort: "Add",
  addNamed: (name: string) => `Add ${name}`,
  hide: "Hide widget",
  resize: "Resize",
  dragToMove: "Drag to move widget",
  refreshing: "Refreshing",
  actions: "Widget actions",
  refresh: "Refresh",
  viewDetails: "View details",
  expand: "Expand widget",
  contentError: "Something went wrong loading this widget.",
  contentEmpty: "Nothing to show yet.",
  spans: { small: "Small", medium: "Medium", large: "Large" },
  reset: "Reset to default",
};

const DashboardEngineLabelsContext = React.createContext<DashboardEngineLabels>(
  DEFAULT_DASHBOARD_ENGINE_LABELS,
);

/** Wrap a dashboard to give its chrome the host app's translated strings. */
export function DashboardEngineLabelsProvider({
  labels,
  children,
}: {
  labels: DashboardEngineLabels;
  children: React.ReactNode;
}) {
  return (
    <DashboardEngineLabelsContext.Provider value={labels}>{children}</DashboardEngineLabelsContext.Provider>
  );
}

export function useDashboardEngineLabels(): DashboardEngineLabels {
  return React.useContext(DashboardEngineLabelsContext);
}
